import type * as Monaco from 'monaco-editor';
import type {
  LockedRange,
  MonacoRangeLockerOptions,
  RangeCalculator,
  DecorationIds,
} from './types';
import { PRESET_CALCULATORS } from './presets';

/**
 * Monaco Range Locker - 核心类
 * 用于在 Monaco Editor 中锁定指定行范围，防止用户编辑
 *
 * @example
 * ```ts
 * const locker = new MonacoRangeLocker({
 *   monaco,
 *   editor,
 *   calculator: 'typescript-function',
 *   onLockViolation: (range) => {
 *     message.warning('此区域已锁定');
 *   }
 * });
 *
 * // 启用锁定
 * locker.enable();
 *
 * // 更新锁定范围
 * locker.updateRanges();
 *
 * // 禁用锁定
 * locker.disable();
 *
 * // 销毁实例
 * locker.dispose();
 * ```
 */
export class MonacoRangeLocker {
  private monaco: typeof Monaco;
  private editor: Monaco.editor.IStandaloneCodeEditor;
  private calculator: RangeCalculator;
  private context?: any;
  private decorationOptions: Required<
    NonNullable<MonacoRangeLockerOptions['decorationOptions']>
  >;
  private onLockViolation?: MonacoRangeLockerOptions['onLockViolation'];
  private enabled: boolean;

  private decorationIds: DecorationIds = { current: [] };
  private lockedRanges: LockedRange[] = [];
  private contentChangeDisposable: Monaco.IDisposable | null = null;

  // 防抖相关
  private lastViolationTime: number = 0;
  private violationDebounceMs: number = 1000; // 默认1秒内只触发一次

  constructor(options: MonacoRangeLockerOptions) {
    this.monaco = options.monaco;
    this.editor = options.editor;
    this.context = options.context;
    this.onLockViolation = options.onLockViolation;
    this.enabled = options.enabled !== false;
    this.violationDebounceMs = options.violationDebounceMs ?? 1000;

    // 解析计算器
    if (typeof options.calculator === 'string') {
      const preset = PRESET_CALCULATORS[options.calculator];
      if (!preset) {
        throw new Error(`Unknown preset calculator: ${options.calculator}`);
      }
      this.calculator = preset;
    } else {
      this.calculator = options.calculator;
    }

    // 设置装饰选项的默认值
    this.decorationOptions = {
      className:
        options.decorationOptions?.className || 'locked-line-decoration',
      glyphMarginClassName:
        options.decorationOptions?.glyphMarginClassName || 'locked-line-glyph',
      hoverMessage:
        options.decorationOptions?.hoverMessage ||
        '🔒 此区域已锁定，不允许修改',
      isWholeLine: options.decorationOptions?.isWholeLine !== false,
    };

    if (this.enabled) {
      this.enable();
    }
  }

  /**
   * 启用锁定功能
   */
  public enable(): void {
    if (this.enabled && this.contentChangeDisposable) {
      return; // 已启用
    }

    this.enabled = true;
    this.updateRanges();
    this.attachEditListener();
  }

  /**
   * 禁用锁定功能
   */
  public disable(): void {
    this.enabled = false;
    this.clearDecorations();
    this.detachEditListener();
  }

  /**
   * 更新锁定范围（重新计算并应用）
   */
  public updateRanges(): void {
    if (!this.enabled) return;

    const code = this.editor.getValue();
    this.lockedRanges = this.calculator(code, this.context);
    this.applyDecorations();
  }

  /**
   * 手动设置锁定范围
   */
  public setRanges(ranges: LockedRange[]): void {
    this.lockedRanges = ranges;
    if (this.enabled) {
      this.applyDecorations();
    }
  }

  /**
   * 获取当前锁定范围
   */
  public getRanges(): LockedRange[] {
    return [...this.lockedRanges];
  }

  /**
   * 更新上下文（会触发范围重新计算）
   */
  public updateContext(context: any): void {
    this.context = context;
    this.updateRanges();
  }

  /**
   * 更新计算器（切换语言时调用，会触发范围重新计算）
   */
  public updateCalculator(calculator: RangeCalculator | string): void {
    if (typeof calculator === 'string') {
      const preset = PRESET_CALCULATORS[calculator];
      if (!preset) {
        throw new Error(`Unknown preset calculator: ${calculator}`);
      }
      this.calculator = preset;
    } else {
      this.calculator = calculator;
    }
    this.updateRanges();
  }

  /**
   * 检查指定行是否在锁定范围内
   */
  public isLineLocked(line: number): boolean {
    return this.isRangeLocked(line, line);
  }

  /**
   * 检查指定范围是否与锁定范围重叠
   */
  public isRangeLocked(startLine: number, endLine: number): boolean {
    return this.lockedRanges.some(
      range =>
        (startLine >= range.startLine && startLine <= range.endLine) ||
        (endLine >= range.startLine && endLine <= range.endLine) ||
        (startLine <= range.startLine && endLine >= range.endLine)
    );
  }

  /**
   * 销毁实例，清理资源
   */
  public dispose(): void {
    this.disable();
    this.lockedRanges = [];
  }

  /**
   * 应用装饰（视觉提示）
   */
  private applyDecorations(): void {
    const decorations = this.lockedRanges.map(range => ({
      range: new this.monaco.Range(range.startLine, 1, range.endLine, 1),
      options: {
        isWholeLine: this.decorationOptions.isWholeLine,
        className: this.decorationOptions.className,
        glyphMarginClassName: this.decorationOptions.glyphMarginClassName,
        hoverMessage: {
          value: range.description || this.decorationOptions.hoverMessage,
        },
      },
    }));

    this.decorationIds.current = this.editor.deltaDecorations(
      this.decorationIds.current,
      decorations
    );
  }

  /**
   * 清除装饰
   */
  private clearDecorations(): void {
    this.decorationIds.current = this.editor.deltaDecorations(
      this.decorationIds.current,
      []
    );
  }

  /**
   * 附加编辑监听器
   */
  private attachEditListener(): void {
    if (this.contentChangeDisposable) {
      return; // 已附加
    }

    this.contentChangeDisposable = this.editor.onDidChangeModelContent(e => {
      if (!this.enabled) return;

      for (const change of e.changes) {
        const startLine = change.range.startLineNumber;
        const endLine = change.range.endLineNumber;

        // 跳过全文档范围的替换（程序化 setValue/pushEditOperations 的特征）
        // 用户手动编辑不会一次替换整个文档，只有外部数据驱动的更新才会
        const model = this.editor.getModel();
        if (model && startLine === 1 && endLine >= model.getLineCount()) {
          continue;
        }

        if (this.isRangeLocked(startLine, endLine)) {
          // 找到被触犯的锁定范围
          const violatedRange = this.lockedRanges.find(
            range =>
              (startLine >= range.startLine && startLine <= range.endLine) ||
              (endLine >= range.startLine && endLine <= range.endLine) ||
              (startLine <= range.startLine && endLine >= range.endLine)
          );

          // 撤销修改
          this.editor.trigger('keyboard', 'undo', null);

          // 防抖：只在指定时间间隔后才触发回调
          const now = Date.now();
          if (this.onLockViolation && violatedRange) {
            if (now - this.lastViolationTime >= this.violationDebounceMs) {
              this.lastViolationTime = now;
              this.onLockViolation(violatedRange, { startLine, endLine });
            }
          }

          break;
        }
      }
    });
  }

  /**
   * 分离编辑监听器
   */
  private detachEditListener(): void {
    if (this.contentChangeDisposable) {
      this.contentChangeDisposable.dispose();
      this.contentChangeDisposable = null;
    }
  }
}
